/**
 * §3 — the instance sends no `Retry-After` and no `X-RateLimit-*` headers, so the policy is
 * inferred rather than negotiated: exponential backoff with jitter, 250 ms → 8 s, 4 attempts, on
 * 429/5xx/network. **Never retry 401/403** — those are policy, not transience, and retrying them
 * is how you get IP-banned.
 */
export interface RetryPolicy {
  /** Total attempts including the first. 4 = one initial try plus three retries. */
  readonly maxAttempts: number;
  readonly baseDelayMs: number;
  readonly maxDelayMs: number;
  /** 0 = no jitter, 1 = full jitter (delay uniform in `(0, capped]`). */
  readonly jitterRatio: number;
}

export const DEFAULT_RETRY_POLICY: RetryPolicy = {
  maxAttempts: 4,
  baseDelayMs: 250,
  maxDelayMs: 8000,
  jitterRatio: 0.5,
};

/**
 * Delay before retry number `attempt` (1-based: `attempt === 1` is the wait after the first
 * failure). `random` is a value in [0, 1) supplied by the caller so the schedule is reproducible
 * in tests (R1 bans neither `Math.random` nor arithmetic — but an untestable retry schedule is
 * an untested one).
 */
export function backoffDelayMs(attempt: number, random: number, policy: RetryPolicy = DEFAULT_RETRY_POLICY): number {
  const step = Math.max(1, Math.floor(attempt));
  const exponential = policy.baseDelayMs * Math.pow(2, step - 1);
  const capped = Math.min(policy.maxDelayMs, exponential);
  const ratio = Math.min(1, Math.max(0, policy.jitterRatio));
  const clampedRandom = Math.min(1, Math.max(0, random));
  return Math.round(capped * (1 - ratio + ratio * clampedRandom));
}
